// Vendas offline do POS guardadas neste dispositivo (localStorage) e sincronizadas quando a ligação voltar
const KEY = "lumora_pos_offline_sales";
const CACHE_KEY = "lumora_pos_cache";

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = (list) => localStorage.setItem(KEY, JSON.stringify(list));
const TWO_DAYS = 2 * 24 * 3600 * 1000;
// Vendas já faturadas há mais de 48h são removidas automaticamente (mantém o armazenamento pequeno)
const prune = (list) => list.filter((s) => s.status !== "synced" || Date.now() - new Date(s.synced_at || s.created_at).getTime() < TWO_DAYS).slice(0, 200);

export const listOfflineSales = (companyId) => {
  const all = prune(read());
  return companyId ? all.filter((s) => !s.company_id || s.company_id === companyId) : all;
};
export const pendingOfflineCount = (companyId) => listOfflineSales(companyId).filter((s) => s.status !== "synced").length;

export const addOfflineSale = (sale) => {
  const rec = { ...sale, local_id: `off_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`, created_at: new Date().toISOString(), status: "pending", attempts: 0, error: null, server_order_id: sale.server_order_id || null, invoice_number: null };
  write(prune([rec, ...read()]));
  return rec;
};
export const updateOfflineSale = (localId, patch) => write(read().map((s) => (s.local_id === localId ? { ...s, ...patch } : s)));
export const removeOfflineSale = (localId) => write(read().filter((s) => s.local_id !== localId));
export const clearSyncedOfflineSales = () => write(read().filter((s) => s.status !== "synced"));

export const savePosCache = (data) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, cached_at: new Date().toISOString() })); } catch { /* storage cheio */ } };
export const loadPosCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; } };

const isNetworkError = (e) => !e.response;

// Sincroniza em sequência (só as vendas da empresa atual): cria o pedido (idempotente via client_ref) e depois paga/fatura. Devolve resumo.
export const syncOfflineSales = async (api, companyId) => {
  const summary = { synced: 0, failed: 0, stoppedOffline: false, invoices: [] };
  for (const sale of listOfflineSales(companyId).filter((s) => s.status !== "synced")) {
    try {
      let orderId = sale.server_order_id;
      if (!orderId) {
        const r = await api("post", "/orders", { ...sale.order, client_ref: sale.local_id });
        orderId = r.data.order_id;
        updateOfflineSale(sale.local_id, { server_order_id: orderId, server_order_number: r.data.order_number });
      } else if (sale.order?.items?.length) {
        // Mesa que já tinha pedido no servidor + artigos adicionados offline → juntar os artigos (idempotente) antes de faturar
        const r = await api("post", "/orders", { ...sale.order, client_ref: sale.local_id });
        orderId = r.data.order_id;
        updateOfflineSale(sale.local_id, { server_order_id: orderId, order: { ...sale.order, items: [] } });
      }
      const p = sale.payment;
      const params = new URLSearchParams({ payment_method: p.method });
      if (p.customer_name) params.append("customer_name", p.customer_name);
      if (p.customer_nif) params.append("customer_nif", p.customer_nif);
      if (p.method === "cash" && p.amount_received) params.append("amount_received", p.amount_received);
      if (p.method === "card" && p.tpa_id) params.append("tpa_id", p.tpa_id);
      const pay = await api("put", `/orders/${orderId}/pay?${params.toString()}`);
      const inv = pay.data?.invoice;
      updateOfflineSale(sale.local_id, { status: "synced", synced_at: new Date().toISOString(), invoice_number: inv?.invoice_number || null, invoice_id: inv?.invoice_id || null, error: null });
      summary.synced += 1;
      if (inv) summary.invoices.push(inv);
    } catch (e) {
      if (isNetworkError(e)) { summary.stoppedOffline = true; break; }
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || `Erro ${e.response?.status}`;
      if (e.response?.status === 400 && /já pago/i.test(msg)) {
        updateOfflineSale(sale.local_id, { status: "synced", synced_at: new Date().toISOString(), error: null });
        summary.synced += 1;
        continue;
      }
      updateOfflineSale(sale.local_id, { status: "error", error: msg, attempts: (sale.attempts || 0) + 1 });
      summary.failed += 1;
    }
  }
  return summary;
};

// ---------- Mesas offline: pedidos de mesa abertos neste dispositivo sem ligação (vão para o servidor/cozinha ao sincronizar) ----------
const TABLES_KEY = "lumora_pos_offline_tables";
const readTables = () => { try { return JSON.parse(localStorage.getItem(TABLES_KEY) || "[]"); } catch { return []; } };
const writeTables = (list) => localStorage.setItem(TABLES_KEY, JSON.stringify(list.slice(0, 100)));

export const listOfflineTableOrders = (companyId) => readTables().filter((o) => !companyId || !o.company_id || o.company_id === companyId);

const lineOf = (i) => ({ item_id: i.item_id || `loc_${Math.random().toString(36).slice(2, 8)}`, product_id: i.product_id, product_name: i.name || i.product_name, quantity: i.quantity, unit: i.stock_unit || i.unit, unit_price: i.price ?? i.unit_price, line_total: (i.price ?? i.unit_price) * i.quantity, notes: i.notes || null, tax_rate: i.tax_rate ?? 14 });
const recalc = (o) => {
  const total = o.items.reduce((s, i) => s + i.line_total, 0);
  const tax = o.items.reduce((s, i) => s + (i.tax_rate > 0 ? i.line_total - i.line_total / (1 + i.tax_rate / 100) : 0), 0);
  return { ...o, total, tax, subtotal: total - tax };
};

// Cria (ou acrescenta a) o pedido offline da mesa; devolve o pedido local atualizado
export const addOfflineTableItems = (tableId, cartItems, meta = {}) => {
  const list = readTables();
  const idx = list.findIndex((o) => o.table_id === tableId && o.company_id === (meta.company_id || null));
  const lines = cartItems.map(lineOf);
  let rec;
  if (idx >= 0) {
    rec = recalc({ ...list[idx], items: [...list[idx].items, ...lines], updated_at: new Date().toISOString() });
    list[idx] = rec;
  } else {
    rec = recalc({ local_id: `offt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`, table_id: tableId, items: lines, status: "open", payment_status: "pending", created_at: new Date().toISOString(), offline: true, ...meta });
    list.unshift(rec);
  }
  writeTables(list);
  return rec;
};
export const removeOfflineTableItem = (localId, itemId) => {
  const list = readTables().map((o) => (o.local_id === localId ? recalc({ ...o, items: o.items.filter((i) => i.item_id !== itemId) }) : o)).filter((o) => o.items.length > 0);
  writeTables(list);
  return list.find((o) => o.local_id === localId) || null;
};
export const removeOfflineTableOrder = (localId) => writeTables(readTables().filter((o) => o.local_id !== localId));

// Pedido local → corpo de POST /orders
export const offlineTableOrderBody = (o) => ({
  table_id: o.table_id, order_type: "dine_in", customer_name: o.customer_name || null, notes: o.notes || null, waiter_id: o.waiter_id || null, waiter_name: o.waiter_name || null, cooking_time: null,
  items: o.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, notes: i.notes || null })),
});

// Envia para o servidor os pedidos de mesa ainda abertos (idempotente via client_ref). Devolve resumo.
export const syncOfflineTableOrders = async (api, companyId) => {
  const summary = { synced: 0, failed: 0, stoppedOffline: false, orders: [] };
  for (const o of listOfflineTableOrders(companyId)) {
    try {
      const r = await api("post", "/orders", { ...offlineTableOrderBody(o), client_ref: o.local_id });
      removeOfflineTableOrder(o.local_id);
      summary.synced += 1;
      summary.orders.push(r.data);
    } catch (e) {
      if (isNetworkError(e)) { summary.stoppedOffline = true; break; }
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || `Erro ${e.response?.status}`;
      writeTables(readTables().map((x) => (x.local_id === o.local_id ? { ...x, error: msg, attempts: (x.attempts || 0) + 1 } : x)));
      summary.failed += 1;
    }
  }
  return summary;
};
